import { api } from "@/api/client";
import type {
  ProjectCreateBody,
  ProjectDto,
  ProjectFilterOptionsPage,
  ProjectFilterOptionsParams,
  ProjectImportMsProjectPreviewResult,
  ProjectNextCodeDto,
  ProjectPatchBody,
  ProjectsListParams,
  ProjectsPage,
} from "@/types/api/projects";
import { PROJECTS_PAGE_SIZE } from "@/types/api/projects";

function joinList(values: string[] | number[] | undefined): string | undefined {
  if (!values?.length) return undefined;
  return values.join(",");
}

export async function getProjectsPage(params: ProjectsListParams = {}): Promise<ProjectsPage> {
  const { data } = await api.get<ProjectsPage>("/v1/projects", {
    params: {
      limit: params.limit ?? PROJECTS_PAGE_SIZE,
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.q ? { q: params.q } : {}),
      ...(params.sort ? { sort: params.sort } : {}),
      ...(params.progressFrom != null ? { progressFrom: params.progressFrom } : {}),
      ...(params.progressTo != null ? { progressTo: params.progressTo } : {}),
      ...(joinList(params.statusKeys) ? { statusKeys: joinList(params.statusKeys) } : {}),
      ...(joinList(params.priorityIds) ? { priorityIds: joinList(params.priorityIds) } : {}),
      ...(joinList(params.responsibleWorkerIds)
        ? { responsibleWorkerIds: joinList(params.responsibleWorkerIds) }
        : {}),
      ...(joinList(params.clients) ? { clients: joinList(params.clients) } : {}),
      ...(params.startDateFrom ? { startDateFrom: params.startDateFrom } : {}),
      ...(params.startDateTo ? { startDateTo: params.startDateTo } : {}),
      ...(params.dueDateFrom ? { dueDateFrom: params.dueDateFrom } : {}),
      ...(params.dueDateTo ? { dueDateTo: params.dueDateTo } : {}),
      ...(params.active !== undefined ? { active: params.active } : {}),
    },
  });
  return data;
}

export async function getProjectFilterOptions(
  params: ProjectFilterOptionsParams,
): Promise<ProjectFilterOptionsPage> {
  const { data } = await api.get<ProjectFilterOptionsPage>("/v1/projects/filter-options", {
    params: {
      type: params.type,
      ...(params.q?.trim() ? { q: params.q.trim() } : {}),
      ...(params.limit ? { limit: params.limit } : {}),
      ...(params.cursor ? { cursor: params.cursor } : {}),
      ...(params.ids?.length ? { ids: params.ids.join(",") } : {}),
    },
  });
  return data;
}

export async function getProject(id: string): Promise<ProjectDto> {
  const { data } = await api.get<ProjectDto>(`/v1/projects/${id}`);
  return data;
}

export async function getNextProjectCode(): Promise<ProjectNextCodeDto> {
  const { data } = await api.get<ProjectNextCodeDto>("/v1/projects/next-code");
  return data;
}

export async function postProject(body: ProjectCreateBody): Promise<ProjectDto> {
  const { data } = await api.post<ProjectDto>("/v1/projects", body);
  return data;
}

export async function postProjectImportMsProjectPreview(
  file: File,
): Promise<ProjectImportMsProjectPreviewResult> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<ProjectImportMsProjectPreviewResult>(
    "/v1/projects/import-msproject/preview",
    formData,
  );
  return data;
}

export async function patchProject(id: string, body: ProjectPatchBody): Promise<ProjectDto> {
  const { data } = await api.patch<ProjectDto>(`/v1/projects/${id}`, body);
  return data;
}

export async function patchProjectActive(
  id: string,
  body: { isActive: boolean },
): Promise<ProjectDto> {
  const { data } = await api.patch<ProjectDto>(`/v1/projects/${id}/active`, body);
  return data;
}

export async function deleteProject(id: string): Promise<void> {
  await api.delete(`/v1/projects/${id}`);
}
